// Methods & Limitations — the reference page behind every inline ⓘ popover.
// It renders straight from the shared help registry (help/content.ts), grouped by
// the ordered HELP_CATEGORIES, so the long-form treatment on this page can never
// drift from the short-form tooltips: same source, two presentations.
//
// SeaState is a go/no-go decision aid, so every entry leads with how the number is
// made and closes with a load-bearing statement of what it CAN'T tell you. The
// popover deep-link (/methods#<anchor>) lands on the matching subsection.

import { HELP } from "../help/content.js";
import { HELP_CATEGORIES, type Explainer, type HelpKey } from "../help/types.js";
import { OPS_PANELS } from "../ui/flags.js";
import type { SiteNavOptions } from "./site.js";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** The concise units · source line, or null when neither is a measured quantity. */
function metaText(e: Explainer): string | null {
  const bits: string[] = [];
  if (e.units !== undefined && e.units.length > 0) bits.push(e.units);
  if (e.source !== undefined && e.source.length > 0) bits.push(e.source);
  return bits.length > 0 ? bits.join(" · ") : null;
}

/** All explainers in a category, deterministically ordered (by display term). */
function entriesFor(category: string): Explainer[] {
  // A flagged-off workspace must not be documented. The page already skips an
  // empty category in both the table of contents and the body, so emptying the
  // category is all it takes — no second code path to keep in step.
  if (category === "operations" && !OPS_PANELS) return [];
  return Object.values(HELP)
    .filter((e) => e.category === category)
    .sort((a, b) => a.term.localeCompare(b.term));
}

/** An in-page link that jumps to another concept's subsection via the SPA router. */
function seeAlsoLink(key: HelpKey, opts: SiteNavOptions): HTMLAnchorElement | null {
  const target = HELP[key];
  if (target === undefined) return null; // registry incomplete — skip quietly
  const a = el("a", undefined, target.term);
  const href = `/methods#${target.anchor}`;
  a.href = href;
  a.addEventListener("click", (ev) => {
    ev.preventDefault();
    opts.navigate(href);
  });
  return a;
}

function renderExplainer(e: Explainer, opts: SiteNavOptions): HTMLElement {
  const sub = el("div", "doc-entry");
  sub.id = e.anchor;

  sub.appendChild(el("h3", undefined, e.term));

  const meta = metaText(e);
  if (meta !== null) sub.appendChild(el("p", "doc-meta", meta));

  for (const para of e.full) sub.appendChild(el("p", undefined, para));

  const limit = el("div", "doc-limit");
  limit.appendChild(el("strong", undefined, "Limitation — "));
  limit.appendChild(document.createTextNode(e.limitation));
  sub.appendChild(limit);

  if (e.related !== undefined && e.related.length > 0) {
    const line = el("p", "doc-seealso");
    line.appendChild(el("span", undefined, "See also: "));
    let wrote = false;
    for (const key of e.related) {
      const link = seeAlsoLink(key, opts);
      if (link === null) continue;
      if (wrote) line.appendChild(document.createTextNode(", "));
      line.appendChild(link);
      wrote = true;
    }
    if (wrote) sub.appendChild(line);
  }

  return sub;
}

export function mountMethods(container: HTMLElement, opts: SiteNavOptions): void {
  document.title = "Methods & Limitations — SeaState";

  const doc = el("div", "doc");

  // 1. Lede — framing, then the prominent not-for-navigation statement (single-sourced).
  const lede = el("div", "doc-lede");
  lede.appendChild(el("h2", undefined, "Methods & Limitations"));
  lede.appendChild(
    el(
      "p",
      undefined,
      "How each figure on the console is computed, and — with equal weight — what it cannot tell you. " +
        "Every product here is a modelled estimate of a chaotic ocean. Read the confidence, not just the number.",
    ),
  );
  const notForNav = HELP["fnd.notForNav"];
  if (notForNav !== undefined) {
    lede.appendChild(el("div", "doc-warn", notForNav.short));
  }
  doc.appendChild(lede);

  // 2. Sticky table of contents — one anchor per category.
  const toc = el("nav", "doc-toc");
  for (const cat of HELP_CATEGORIES) {
    if (entriesFor(cat.key).length === 0) continue; // nothing to jump to yet
    const a = el("a", undefined, cat.label);
    const href = `/methods#${cat.key}`;
    a.href = href;
    a.addEventListener("click", (ev) => {
      ev.preventDefault();
      opts.navigate(href);
    });
    toc.appendChild(a);
  }
  doc.appendChild(toc);

  // 3. One section per category, in HELP_CATEGORIES order.
  for (const cat of HELP_CATEGORIES) {
    const entries = entriesFor(cat.key);
    if (entries.length === 0) continue;
    const section = el("section", "doc-section");
    section.id = cat.key; // #<category-key> deep-links land on the section heading
    section.appendChild(el("h2", undefined, cat.label));
    for (const e of entries) section.appendChild(renderExplainer(e, opts));
    doc.appendChild(section);
  }

  container.replaceChildren(doc);

  // 4. On mount, honour a deep-link hash — scroll the target into view if present.
  const hash = window.location.hash.replace(/^#/, "");
  if (hash.length > 0) {
    const target = document.getElementById(hash);
    if (target !== null) target.scrollIntoView({ block: "start" });
  }
}
